import { isMapEditing } from './editlock';
// ADS-B contacts on the main map (migration 048). Always starts OFF.
//
// GeoJSON, not vector tiles, and that is the opposite call from firms.ts on
// purpose. Hotspots are a 60-day store where the same tile is valid for
// minutes and Martin's cache earns its keep; aircraft positions turn over on
// every 60 s poll, so a tile keyed by z/x/y would be stale the moment it was
// written and the cache would be pure overhead. The payload is small for the
// same reason the server clips at write time — you only ever receive the
// ground somebody configured a watch for.
//
// TWO FEATURES PER CONTACT, deliberately: a Point for where it is now and a
// LineString for where it has been. They are separate features rather than one
// with a geometry collection because MapLibre needs them on different layers
// (a symbol and a line), and splitting in the browser would mean re-walking
// the payload every poll to do what one SQL pass already did.
//
// SCOPE FOLLOWS THE STORY PICKER. Coverage is per-story config on the server,
// so "which aircraft" is answered by the same selection that narrows the map
// and the panes — a story-scoped map showing another theatre's tankers would
// be the one thing this feature must not do.

import * as maplibregl from 'maplibre-gl';
import * as aircraft3d from './aircraft3d';
import { registerHover } from './hover';

const SRC = 'aircraft';
const TRAIL_LAYER = 'aircraft-trails';
const ICON_LAYER = 'aircraft-icons';
const LABEL_LAYER = 'aircraft-labels';

// Matches the server's POLL_S. Asking faster cannot produce a newer position —
// it would just re-read the same rows.
const POLL_MS = 60 * 1000;

// How much history the trail carries. Longer looks impressive and reads badly:
// at theatre zoom a 6-hour tanker orbit is a solid scribble that hides where
// the aircraft actually is.
const TRAIL_MIN = 30;

// Military and civil are the only distinction the server sends (dbFlags bit 0),
// and it is the one worth colouring: a wildfire story's water bombers are civil
// and its story is about them, so this is a legend cue, never a filter.
const MIL_COLOR = '#e8913c';
const CIV_COLOR = '#5fa8d3';

// The TRAIL colours by ALTITUDE instead — the question a track actually
// raises is "descending toward what?" / "holding at cruise?", and mil vs
// civil already lives on the icon. The ramp is the flight-tracker
// convention: warm on the deck, green in the climb, cool at cruise, violet
// above it — so a reader arriving from any other ADS-B app can read it
// without a legend.
const ALT_STOPS: Array<[number, string]> = [
  [0, '#cf5334'], [4000, '#e0913c'], [10000, '#ddc83d'],
  [20000, '#63b45a'], [30000, '#3fa7cc'], [42000, '#8e6fe0'],
];

const ALT_RGB = ALT_STOPS.map(([alt, hex]) => {
  const n = parseInt(hex.slice(1), 16);
  return [alt, [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255]] as
    [number, [number, number, number]];
});

/** The SAME ramp the 2D line-color expression runs, evaluated in JS — the 3D
 *  ribbon shades its own vertices, so the two views reading one constant is
 *  what keeps a trail the same colour flat and pitched. */
export function altColor(altFt: number): [number, number, number] {
  const first = ALT_RGB[0]!, last = ALT_RGB[ALT_RGB.length - 1]!;
  if (altFt <= first[0]) return first[1];
  if (altFt >= last[0]) return last[1];
  for (let i = 1; i < ALT_RGB.length; i++) {
    const [b, cb] = ALT_RGB[i]!;
    if (altFt > b) continue;
    const [a, ca] = ALT_RGB[i - 1]!;
    const t = (altFt - a) / (b - a);
    return [ca[0] + (cb[0] - ca[0]) * t, ca[1] + (cb[1] - ca[1]) * t,
            ca[2] + (cb[2] - ca[2]) * t];
  }
  return last[1];
}

const EMPTY_FC = { type: 'FeatureCollection', features: [] as any[] };

let mapRef: maplibregl.Map | null = null;
let enabled = false;
let storyId: string | null = null;
/** Which upstream served the positions, for the popup's tracker link. The
 *  server says (`coverage.provider`), so the link follows the configured
 *  provider instead of naming one this deployment may no longer use. */
let provider = { name: 'adsb.fi', globe: 'https://globe.adsb.fi/?icao=' };
let timer: number | null = null;
let inflight: AbortController | null = null;
let count = 0;

/** Why the layer has what it has. `unconfigured` and `no-ground` are operator
 *  problems with an answer in the admin console; `empty` is the honest normal
 *  state over a theatre with thin receiver coverage. */
export type AircraftStatus = 'off' | 'unconfigured' | 'no-ground' | 'empty' | 'ok';
let status: AircraftStatus = 'off';
export function getStatus(): AircraftStatus { return status; }

const listeners = new Set<(n: number) => void>();

/** A plane silhouette pointing NORTH, so icon-rotate can take the reported
 *  track directly. Drawn rather than shipped as an asset for raster.ts's
 *  reason: one small canvas beats a binary in the bundle and a fetch. */
function drawPlane(color: string): ImageData {
  const S = 40;
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  const ctx = c.getContext('2d')!;
  ctx.translate(S / 2, S / 2);
  ctx.beginPath();
  ctx.moveTo(0, -16);          // nose
  ctx.lineTo(3.5, -6);
  ctx.lineTo(16, 3);           // starboard wing
  ctx.lineTo(16, 6.5);
  ctx.lineTo(3.5, 3);
  ctx.lineTo(3, 11);
  ctx.lineTo(8, 15);
  ctx.lineTo(8, 17);
  ctx.lineTo(0, 14.5);         // tail
  ctx.lineTo(-8, 17);
  ctx.lineTo(-8, 15);
  ctx.lineTo(-3, 11);
  ctx.lineTo(-3.5, 3);
  ctx.lineTo(-16, 6.5);
  ctx.lineTo(-16, 3);
  ctx.lineTo(-3.5, -6);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  // The dark rim is what keeps a contact readable over both a bright desert
  // basemap and a dark one — the same trick the FIRMS triangle uses.
  ctx.lineWidth = 2.2;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(15,28,39,.9)';
  ctx.stroke();
  return ctx.getImageData(0, 0, S, S);
}

export function installAircraftImages(map: maplibregl.Map): void {
  for (const [id, color] of [['ac-mil', MIL_COLOR], ['ac-civ', CIV_COLOR]] as const) {
    if (!map.hasImage(id)) map.addImage(id, drawPlane(color), { pixelRatio: 2 });
  }
}

/** The layer definitions, exported as a pure function so objects.test.ts's
 *  style-spec validation can reach them without constructing a map — the
 *  trap that file exists for is that a malformed expression throws inside
 *  addLayer and takes the WHOLE map down, not just this overlay. */
export function aircraftLayers(): maplibregl.LayerSpecification[] {
  return [
    {
      id: TRAIL_LAYER,
      type: 'line',
      source: SRC,
      filter: ['==', ['get', 'trail'], true],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // Data-driven on the SEGMENT's altitude (trailSegments splits each
        // trail into per-fix segments precisely so this can vary along the
        // track). coalesce absorbs a fix with no altitude as ground-coloured
        // rather than the black fallback a null would paint.
        'line-color': ['interpolate', ['linear'],
                       ['coalesce', ['get', 'alt'], 0],
                       ...ALT_STOPS.flat()] as never,
        'line-opacity': 0.65,
        // Zoom ramp OUTSIDE, per-feature get INSIDE — `zoom` may only be the
        // input to a top-level interpolate, so the obvious nesting is illegal
        // and throws at addLayer time (objects.test.ts).
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.4, 10, 3],
      },
    },
    {
      id: ICON_LAYER,
      type: 'symbol',
      source: SRC,
      filter: ['!=', ['get', 'trail'], true],
      layout: {
        'icon-image': ['case', ['==', ['get', 'mil'], true], 'ac-mil', 'ac-civ'],
        'icon-rotate': ['coalesce', ['get', 'track'], 0],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.65, 10, 1.15],
      },
      paint: {
        // A contact going quiet FADES rather than vanishing — the feed's rule
        // for aged events, applied to the map. Blinking out between polls
        // reads as "it landed", which is a claim the data cannot make.
        'icon-opacity': ['case', ['==', ['get', 'stale'], true], 0.35, 1],
      },
    },
    {
      id: LABEL_LAYER,
      type: 'symbol',
      source: SRC,
      filter: ['!=', ['get', 'trail'], true],
      minzoom: 6,
      layout: {
        // Callsign if it has one, else the registration, else the type. A
        // contact with none of the three is still worth drawing — the icon
        // says something is there — so the label degrades instead of the
        // feature disappearing.
        'text-field': ['coalesce', ['get', 'flight'], ['get', 'reg'],
                       ['get', 'type'], ''],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-offset': [0, 1.35],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': ['case', ['==', ['get', 'mil'], true], MIL_COLOR, CIV_COLOR],
        'text-halo-color': 'rgba(10,18,26,.85)',
        'text-halo-width': 1.4,
        'text-opacity': ['case', ['==', ['get', 'stale'], true], 0.4, 1],
      },
    },
  ];
}

export const AIRCRAFT_LAYER_IDS = [TRAIL_LAYER, ICON_LAYER, LABEL_LAYER];

// How many pieces each 60 s leg is subdivided into by the spline. A jet
// covers ~15 km between polls, so the raw polyline is visibly angular at
// theatre zoom; six subdivisions puts a vertex every ~2.5 km, below what the
// eye resolves as a corner at any zoom the layer is read at.
const SUB = 6;

function crPoint(p0: number[], p1: number[], p2: number[], p3: number[],
                 t: number): number[] {
  const t2 = t * t, t3 = t2 * t;
  const f = (a: number, b: number, c: number, d: number) =>
    0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2
           + (-a + 3 * b - 3 * c + d) * t3);
  return [f(p0[0]!, p1[0]!, p2[0]!, p3[0]!), f(p0[1]!, p1[1]!, p2[1]!, p3[1]!)];
}

/** A trail LineString -> one smoothed feature PER LEG, carrying that leg's
 *  altitude. Split rather than one long line because line-color can vary per
 *  FEATURE only — a single feature is one colour, and "descending" is the
 *  whole point of colouring by altitude. A Catmull-Rom pass through the fixes
 *  keeps every original point (the curve interpolates, never approximates —
 *  the drawn track still passes through what was actually reported). */
function prepPath(coords: number[][], alts: Array<number | null>):
    { pts: number[][]; filled: Array<number | null> } {
  // Two queries can observe the same aircraft ~a second apart (a K35R is both
  // `mil` and `type=K35R`), landing near-duplicate vertices that kink the
  // spline. ~50 m at the equator; anything nearer adds no track.
  const pts: number[][] = [];
  const raw: Array<number | null> = [];
  coords.forEach((c, i) => {
    const prev = pts[pts.length - 1];
    if (prev && Math.abs(c[0]! - prev[0]!) < 5e-4
             && Math.abs(c[1]! - prev[1]!) < 5e-4) return;
    const alt = alts[i];
    pts.push(c);
    raw.push(typeof alt === 'number' ? alt : null);
  });
  // A fix with no altitude borrows its neighbour's (forward, then backward
  // for a leading gap) — a one-fix hole should not flash ground-red through
  // an otherwise-cruising trail.
  const filled = [...raw];
  for (let i = 1; i < filled.length; i++) filled[i] ??= filled[i - 1] ?? null;
  for (let i = filled.length - 2; i >= 0; i--) filled[i] ??= filled[i + 1] ?? null;
  return { pts, filled };
}

export function trailSegments(coords: number[][], alts: Array<number | null>,
                              mil: boolean): object[] {
  const { pts, filled } = prepPath(coords, alts);
  if (pts.length < 2) return [];
  const out: object[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i]!, p2 = pts[i + 1]!;
    const p0 = pts[i - 1] ?? p1;
    const p3 = pts[i + 2] ?? p2;
    const line: number[][] = [];
    for (let k = 0; k <= SUB; k++) {
      line.push(crPoint(p0, p1, p2, p3, k / SUB));
    }
    const a = filled[i] ?? null, b = filled[i + 1] ?? null;
    out.push({
      type: 'Feature',
      properties: { trail: true, mil,
                    alt: a != null && b != null ? (a + b) / 2 : a ?? b },
      geometry: { type: 'LineString', coordinates: line },
    });
  }
  return out;
}

/** The same spline as one continuous path with a lerped altitude per vertex —
 *  what the 3D ribbon consumes. Same prep, same Catmull-Rom, so the flat and
 *  pitched trails are the same curve. */
export function smoothPathAlt(coords: number[][], alts: Array<number | null>):
    { pts: Array<[number, number]>; altFt: number[] } {
  const { pts, filled } = prepPath(coords, alts);
  if (pts.length < 2) return { pts: [], altFt: [] };
  const opts: Array<[number, number]> = [];
  const oalt: number[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i]!, p2 = pts[i + 1]!;
    const p0 = pts[i - 1] ?? p1;
    const p3 = pts[i + 2] ?? p2;
    const a = filled[i] ?? 0, b = filled[i + 1] ?? 0;
    const kMax = i === pts.length - 2 ? SUB : SUB - 1;
    for (let k = 0; k <= kMax; k++) {
      const t = k / SUB;
      const p = crPoint(p0, p1, p2, p3, t);
      opts.push([p[0]!, p[1]!]);
      oalt.push(a + (b - a) * t);
    }
  }
  return { pts: opts, altFt: oalt };
}

/** Great-circle course from a to b, degrees clockwise from north. */
export function bearingDeg(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const p1 = a[1] * rad, p2 = b[1] * rad, dl = (b[0] - a[0]) * rad;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) / rad + 360) % 360;
}

/** The heading a contact should be DRAWN with.
 *
 *  The server already falls back from course-over-ground to true/magnetic
 *  heading. What is left is a contact with neither — and defaulting those to
 *  0 points them at north, which on a map is indistinguishable from a broken
 *  rotation. Its own recent track answers better than north does: the last
 *  trail leg is where it was actually going. Returns null when even that is
 *  unavailable (a single fix, standing still), and the caller leaves the
 *  rotation alone rather than inventing one. */
export function headingFor(props: any, trail?: number[][]): number | null {
  if (typeof props?.track === 'number') return props.track;
  if (trail && trail.length >= 2) {
    const b = trail[trail.length - 1] as [number, number];
    const a = trail[trail.length - 2] as [number, number];
    if (a[0] !== b[0] || a[1] !== b[1]) return bearingDeg(a, b);
  }
  return null;
}

/** The server payload, reshaped for display: contacts pass through (with the
 *  heading resolved), each trail explodes into its smoothed altitude
 *  segments. */
function displayFeatures(fc: any): any {
  const trails = new Map<string, number[][]>();
  for (const f of fc.features ?? []) {
    if (f.properties?.trail && f.geometry?.type === 'LineString') {
      trails.set(String(f.properties.hex), f.geometry.coordinates);
    }
  }
  const out: any[] = [];
  for (const f of fc.features ?? []) {
    if (f.properties?.trail && f.geometry?.type === 'LineString') {
      out.push(...trailSegments(f.geometry.coordinates,
                                f.properties.alts ?? [],
                                f.properties.mil === true));
    } else {
      const track = headingFor(f.properties, trails.get(String(f.properties?.hex)));
      out.push(track === null ? f
        : { ...f, properties: { ...f.properties, track } });
    }
  }
  return { ...fc, features: out };
}

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

function popupHtml(p: Record<string, any>): string {
  const name = p.flight || p.reg || p.type || p.hex;
  const alt = typeof p.alt === 'number'
    ? (p.alt === 0 ? 'ground / 0 ft' : `${Math.round(p.alt).toLocaleString()} ft`) : '—';
  const gs = typeof p.gs === 'number'
    ? `${Math.round(p.gs)} kn · ${Math.round(p.gs * 1.852)} km/h` : '—';
  const trk = typeof p.track === 'number' ? `${Math.round(p.track)}°` : '—';
  const age = typeof p.ageS === 'number'
    ? (p.ageS < 90 ? `${Math.round(p.ageS)} s ago` : `${Math.round(p.ageS / 60)} min ago`)
    : '—';
  // A replayed contact says WHEN its last fix was, not how long ago — "12 min
  // ago" measured from a playhead three days back is a number nobody wants.
  const seen = typeof p.asOf === 'number' && typeof p.ageS === 'number'
    ? new Date(p.asOf - p.ageS * 1000).toLocaleString(undefined, {
        weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : age;
  return `<div class="ac-popup">
    <h4>${esc(name)} ${p.mil === true ? '<span class="ap-mil">military</span>' : ''}</h4>
    <div class="ap-sub">${esc(p.desc || p.type || 'Unknown type')}</div>
    <dl>
      <dt>Altitude</dt><dd>${esc(alt)}</dd>
      <dt>Speed</dt><dd>${esc(gs)}</dd>
      <dt>Heading</dt><dd>${esc(trk)}</dd>
      <dt>Squawk</dt><dd>${esc(p.squawk || '—')}</dd>
      <dt>Registration</dt><dd>${esc(p.reg || '—')}</dd>
      <dt>ICAO hex</dt><dd>${esc(p.hex || '—')}</dd>
      <dt>Last seen</dt><dd>${esc(seen)}${p.stale === true ? ' · stale' : ''}</dd>
    </dl>
    <p class="ap-link"><a href="${esc(provider.globe)}${encodeURIComponent(String(p.hex ?? ''))}"
      target="_blank" rel="noopener">Track on ${esc(provider.name)} ↗</a></p>
  </div>`;
}

let clickReady = false;
let mode3d = false;
let lastFc: any = null;

/** One place decides which of the two renderings is on screen. The globe
 *  check makes the projection toggle degrade to the flat layers rather than
 *  vanishing the planes — aircraft3d bails on globe for domes.ts's reason. */
function applyVisibility(map: maplibregl.Map): void {
  const globe = map.getProjection()?.type === 'globe';
  // The layers show for the live overlay OR the transport's replay toggle;
  // paint() decides which picture they hold.
  const shown = enabled || replayOn;
  const flat = shown && (!mode3d || globe);
  const vis = (id: string, on: boolean) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  };
  vis(TRAIL_LAYER, flat);
  vis(ICON_LAYER, flat);
  // The label stays in both views: in 3D it tags the stalk's ground point, so
  // a crowded sky still says which stalk is whose.
  vis(LABEL_LAYER, shown);
  aircraft3d.setVisible(shown && mode3d && !globe);
}

/** What the source holds right now: the replay slice while the playhead is
 *  in history, else the live picture — or nothing, when the live overlay is
 *  off and the transport sits at "live". One place, so a live poll landing
 *  mid-replay cannot overwrite the replayed sky with today's. */
function paint(): void {
  const map = mapRef;
  if (!map) return;
  const src = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  const fc = replayOn && replayAt !== null ? replayFeatures(history, replayAt)
    : enabled && lastFc ? lastFc : EMPTY_FC;
  src.setData(displayFeatures(fc));
  if (mode3d) push3d(fc);
}

function push3d(fc: any): void {
  const contacts: aircraft3d.Contact3d[] = [];
  const paths: aircraft3d.Path3d[] = [];
  const trails = new Map<string, number[][]>();
  for (const f of fc?.features ?? []) {
    if (f.properties?.trail && f.geometry?.type === 'LineString') {
      trails.set(String(f.properties.hex), f.geometry.coordinates);
    }
  }
  for (const f of fc?.features ?? []) {
    const p = f.properties ?? {};
    if (p.trail && f.geometry?.type === 'LineString') {
      const { pts, altFt } = smoothPathAlt(f.geometry.coordinates, p.alts ?? []);
      if (pts.length < 2) continue;
      paths.push({ pts, altM: altFt.map((a) => a * 0.3048),
                   rgb: altFt.map((a) => altColor(a)) });
    } else if (f.geometry?.type === 'Point') {
      contacts.push({
        lon: f.geometry.coordinates[0], lat: f.geometry.coordinates[1],
        // Feet to metres: the upstream reports barometric altitude in feet
        // and MercatorCoordinate.fromLngLat takes metres. Getting this wrong
        // is a 3.3x error that still looks plausible on screen.
        altM: (typeof p.alt === 'number' ? p.alt : 0) * 0.3048,
        trackDeg: headingFor(p, trails.get(String(p.hex))) ?? 0,
        mil: p.mil === true,
        alpha: p.stale === true ? 0.35 : 1,
        props: p,
      });
    }
  }
  aircraft3d.setData(contacts, paths);
  mapRef?.triggerRepaint();
}

/** The Layers panel's "3D flight view" toggle. Idempotent; safe before the
 *  layer is enabled (it only decides which rendering an enabled layer uses). */
export function set3d(map: maplibregl.Map, on: boolean): void {
  mapRef = map;
  mode3d = on;
  if (on) {
    if (!aircraft3d.isInstalled(map)) aircraft3d.install(map);
    paint();
    // A flat-down 3D view reads identically to 2D; give the first toggle a
    // tilt. Never on the way out — the operator may have pitched deliberately.
    if (enabled && map.getPitch() < 25) map.easeTo({ pitch: 55, duration: 700 });
  }
  applyVisibility(map);
  map.triggerRepaint();
}

function ensureLayers(map: maplibregl.Map): void {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  installAircraftImages(map);
  for (const spec of aircraftLayers()) {
    if (!map.getLayer(spec.id)) map.addLayer(spec);
  }
  if (!clickReady) {
    // NOTE maplibre: never chain .addTo() after Popup.on() — firms.ts's rule.
    map.on('click', ICON_LAYER, (e) => {
      if (isMapEditing()) return;
      const f = e.features?.[0];
      if (!f || f.geometry.type !== 'Point') return;
      const popup = new maplibregl.Popup({ closeButton: true, maxWidth: '280px' });
      popup.setLngLat(f.geometry.coordinates as [number, number]);
      popup.setHTML(popupHtml(f.properties as Record<string, any>));
      popup.addTo(map);
    });
    registerHover(map, { layers: [ICON_LAYER], cursor: 'pointer' });
    // In 3D the icon layer is hidden, so the sprite answers through a
    // screen-space hit test against where the last frame actually drew it.
    map.on('click', (e) => {
      if (isMapEditing() || !mode3d || !enabled) return;
      const hit = aircraft3d.contactAt(map, e.point.x, e.point.y);
      if (!hit) return;
      const popup = new maplibregl.Popup({ closeButton: true, maxWidth: '280px' });
      popup.setLngLat(hit.lngLat);
      popup.setHTML(popupHtml(hit.props as Record<string, any>));
      popup.addTo(map);
    });
    let cursor3d = false;
    map.on('mousemove', (e) => {
      const want = !isMapEditing() && mode3d && enabled
        && !!aircraft3d.contactAt(map, e.point.x, e.point.y);
      if (want !== cursor3d) {
        cursor3d = want;
        // Only ever touch the cursor we set ourselves, or this fights the
        // layer-scoped mouseenter handlers above and on other overlays.
        if (want) map.getCanvas().style.cursor = 'pointer';
        else if (map.getCanvas().style.cursor === 'pointer') map.getCanvas().style.cursor = '';
      }
    });
    clickReady = true;
  }
}

async function refresh(): Promise<void> {
  if (!enabled || !mapRef) return;
  inflight?.abort();
  const ctl = new AbortController();
  inflight = ctl;
  const qs = new URLSearchParams({ trail: String(TRAIL_MIN) });
  if (storyId) qs.set('story', storyId);
  try {
    const res = await fetch(`/api/aircraft?${qs}`, { signal: ctl.signal });
    if (!res.ok) return;
    const fc = await res.json();
    lastFc = fc;
    paint();
    applyVisibility(mapRef);
    // Contacts, not features — the payload carries a trail per contact and
    // counting features would report roughly double and move whenever the
    // trail length changed.
    count = (fc.features ?? []).filter((f: any) => !f.properties?.trail).length;
    // WHY it is zero, not just that it is. Three states look identical as a
    // bare 0 — nothing configured, configured over ground that resolves to
    // nothing (a story with no area and no drawn zone), or configured,
    // correct, and the sky is empty. Over the Gulf the third is the NORMAL
    // state, so a reader with no way to tell them apart reasonably concludes
    // the layer is broken.
    const cov = fc.coverage ?? {};
    if (typeof cov.provider?.name === 'string' && typeof cov.provider?.globe === 'string') {
      provider = { name: cov.provider.name, globe: cov.provider.globe };
    }
    status = cov.watches === 0 ? 'unconfigured'
      : cov.withGround === 0 ? 'no-ground'
        : count === 0 ? 'empty' : 'ok';
    for (const cb of listeners) cb(count);
  } catch {
    // An aborted or failed poll leaves the previous positions on the map.
    // Blanking them would say "nothing is flying", which is a different claim
    // from "we could not ask".
  }
}

export function setEnabled(map: maplibregl.Map, on: boolean): void {
  mapRef = map;
  enabled = on;
  if (on) {
    ensureLayers(map);
    applyVisibility(map);
    void refresh();
    if (timer === null) timer = window.setInterval(refresh, POLL_MS);
  } else {
    applyVisibility(map);
    if (timer !== null) { window.clearInterval(timer); timer = null; }
    inflight?.abort();
    // The polling STOPS when the layer is off. This is a shared free upstream
    // with no SLA behind the server's poller, and a browser tab left open for
    // days is exactly this codebase's standing assumption.
    count = 0;
    status = 'off';
    for (const cb of listeners) cb(count);
    paint();   // the live picture leaves; a running replay keeps its own
  }
}

/** Follow the topbar story scope. Refetches immediately rather than waiting
 *  out the poll, because a scope change is a deliberate act and a minute of
 *  the previous story's aircraft is indistinguishable from a bug. */
export function setStory(id: string | null): void {
  if (storyId === id) return;
  storyId = id;
  if (enabled) void refresh();
  if (replayOn) void loadHistory();
}

export function getCount(): number {
  return count;
}

export function onCountChange(cb: (n: number) => void): () => void {
  listeners.add(cb);
  cb(count);
  return () => listeners.delete(cb);
}

// --- replay ------------------------------------------------------------------
//
// THE TIMELINE REPLAYS FLIGHT PATHS FROM THE 7-DAY RECORD. The live layer
// polls /api/aircraft for "now"; the transport instead loads every fix in its
// window ONCE (/api/aircraft/history) and slices it per playhead here — at
// ×900 a fetch per tick would be a request storm against our own database
// for a picture the client can cut itself. Each contact is drawn where it
// was at the playhead, interpolated between its bracketing fixes so a jet
// MOVES rather than jumping between 60 s polls, with the same 30-minute
// trail behind it and the same fade when the record goes quiet. It is its
// own toggle in the transport's Layers popover, independent of the Layers
// panel's live overlay — replaying last night's tanker orbit should not
// require watching today's sky.

export interface HistoryFix {
  t: number; lon: number; lat: number;
  alt: number | null; track: number | null; gs: number | null;
}
export interface HistoryContact {
  hex: string; flight: string | null; reg: string | null; type: string | null;
  desc: string | null; mil: boolean;
  /** Ascending by `t`. */
  fixes: HistoryFix[];
}
export interface ContactSnapshot {
  lon: number; lat: number; alt: number | null; track: number | null;
  gs: number | null; stale: boolean; ageS: number;
  trail: number[][]; alts: Array<number | null>;
}

// The server's STALE_POS_S: a contact whose last fix is older than this at
// the playhead FADES, exactly as the live layer fades a contact going quiet.
const STALE_MS = 300_000;
// No fix inside the trail window means the contact is not drawn at all — it
// has left the record, and holding its last position would claim a loiter
// the data never reported.
const GONE_MS = TRAIL_MIN * 60_000;
// Two fixes further apart than this are not one continuous track to
// interpolate along — a coverage gap, or a contact that went dark and came
// back — so the position holds at the last fix instead of gliding across
// the gap in a straight line the aircraft never flew.
const LERP_MAX_MS = STALE_MS;

/** Where a contact was at `at`, from its fix record — pure, so the slice
 *  rule is a test rather than a screenshot. Null when nothing in the record
 *  falls inside the trail window before `at`. */
export function contactAt(c: HistoryContact, at: number,
                          trailMs = GONE_MS, staleMs = STALE_MS): ContactSnapshot | null {
  const fixes = c.fixes;
  // Last fix at or before `at` — a binary search, since a 7-day record runs
  // ~10k fixes per contact and this runs per contact per tick.
  let lo = 0, hi = fixes.length - 1, i = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (fixes[mid]!.t <= at) { i = mid; lo = mid + 1; } else hi = mid - 1;
  }
  if (i < 0) return null;
  const last = fixes[i]!;
  const age = at - last.t;
  if (age > trailMs) return null;
  const next = fixes[i + 1];
  let lon = last.lon, lat = last.lat, alt = last.alt;
  if (next && next.t > last.t && next.t - last.t <= LERP_MAX_MS) {
    const f = (at - last.t) / (next.t - last.t);
    lon = last.lon + (next.lon - last.lon) * f;
    lat = last.lat + (next.lat - last.lat) * f;
    if (last.alt != null && next.alt != null) alt = last.alt + (next.alt - last.alt) * f;
  }
  const trail: number[][] = [];
  const alts: Array<number | null> = [];
  for (let k = i; k >= 0 && at - fixes[k]!.t <= trailMs; k--) {
    trail.push([fixes[k]!.lon, fixes[k]!.lat]);
    alts.push(fixes[k]!.alt);
  }
  trail.reverse();
  alts.reverse();
  // The trail ends at the DRAWN position, so the line reaches the icon
  // mid-leg instead of stopping one fix behind it.
  if (lon !== last.lon || lat !== last.lat) {
    trail.push([lon, lat]);
    alts.push(alt);
  }
  return { lon, lat, alt, gs: last.gs, track: last.track, stale: age > staleMs,
           ageS: age / 1000, trail, alts };
}

/** The server's `contacts` → typed records. Fixes arrive as arrays
 *  [t_ms, lon, lat, alt, track, gs] to keep a 7-day payload small. */
export function parseHistory(body: any): HistoryContact[] {
  return (body?.contacts ?? []).map((c: any): HistoryContact => ({
    hex: String(c.hex), flight: c.flight ?? null, reg: c.reg ?? null,
    type: c.type ?? null, desc: c.desc ?? null, mil: c.mil === true,
    fixes: (c.fixes ?? []).map((f: any[]): HistoryFix => ({
      t: Number(f[0]), lon: Number(f[1]), lat: Number(f[2]),
      alt: typeof f[3] === 'number' ? f[3] : null,
      track: typeof f[4] === 'number' ? f[4] : null,
      gs: typeof f[5] === 'number' ? f[5] : null,
    })),
  }));
}

/** The window's contacts as they stood at `at`, in the SAME feature shape
 *  the live endpoint emits — so displayFeatures, push3d and the popup serve
 *  both pictures with no second path. */
export function replayFeatures(contacts: HistoryContact[], at: number): any {
  const feats: any[] = [];
  for (const c of contacts) {
    const s = contactAt(c, at);
    if (!s) continue;
    feats.push({
      type: 'Feature',
      properties: { hex: c.hex, flight: c.flight, reg: c.reg, type: c.type,
                    desc: c.desc, mil: c.mil, alt: s.alt, gs: s.gs,
                    track: s.track, stale: s.stale, ageS: Math.round(s.ageS),
                    asOf: at, fixes: s.trail.length },
      geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
    });
    if (s.trail.length > 1) {
      feats.push({
        type: 'Feature',
        properties: { hex: c.hex, trail: true, type: c.type, mil: c.mil,
                      alts: s.alts },
        geometry: { type: 'LineString', coordinates: s.trail },
      });
    }
  }
  return { type: 'FeatureCollection', features: feats };
}

let replayOn = false;
let replayWin: { from: number; to: number } | null = null;
let replayAt: number | null = null;
let history: HistoryContact[] = [];
let historyInflight: AbortController | null = null;
let historyTimer: number | undefined;

async function loadHistory(): Promise<void> {
  const win = replayWin;
  if (!win || !mapRef) return;
  historyInflight?.abort();
  const ctl = new AbortController();
  historyInflight = ctl;
  const qs = new URLSearchParams({ from: String(Math.floor(win.from / 1000)),
                                   to: String(Math.ceil(win.to / 1000)) });
  if (storyId) qs.set('story', storyId);
  try {
    const res = await fetch(`/api/aircraft/history?${qs}`, { signal: ctl.signal });
    if (!res.ok) return;
    const body = await res.json();
    if (replayWin !== win) return;    // a newer window is already in flight
    history = parseHistory(body);
    paint();
  } catch {
    // An aborted or failed load keeps whatever was drawn — the same rule as
    // the live poll: "could not ask" is not "nothing flew".
  }
}

/** The transport's Aircraft toggle and window. Called from every
 *  timeline.apply(), so the window it names is always the current one; a
 *  changed window reloads the record, debounced, since dragging a handle
 *  changes it on every pointer move. `on` false hands the layers back to the
 *  live overlay's own state. */
export function setReplay(map: maplibregl.Map, on: boolean,
                          from: number, to: number): void {
  mapRef = map;
  if (!on) {
    if (!replayOn) return;
    replayOn = false;
    replayWin = null;
    history = [];
    historyInflight?.abort();
    window.clearTimeout(historyTimer);
    applyVisibility(map);
    paint();
    return;
  }
  if (!replayOn) {
    replayOn = true;
    ensureLayers(map);
    applyVisibility(map);
  }
  if (!replayWin || replayWin.from !== from || replayWin.to !== to) {
    replayWin = { from, to };
    window.clearTimeout(historyTimer);
    historyTimer = window.setTimeout(() => void loadHistory(), 400);
  }
}

/** The playhead; null is "live", where the layers show the live overlay's
 *  own picture (or nothing, when that overlay is off). */
export function setReplayAt(at: number | null): void {
  if (replayAt === at) return;
  replayAt = at;
  if (replayOn) paint();
}
